import cx from 'classnames';
import { useCallback, useEffect, type FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { dontUpsell } from '@trello/browser';
import { Button } from '@trello/nachos/button';
import type { OnboardingIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { PremiumLozenge } from 'app/src/components/FreeTrial';
import { usePersonalProductivityCohortValue } from 'app/src/components/PersonalProductivityBeta';
import premiumTrialModalPPWelcomeImageSrc from './premium-trial-modal-pp-welcome.png';
import premiumTrialModalWelcomeImagePngSrc from './premium-trial-modal-welcome-image.png';

import * as styles from './PremiumTrialModal.module.less';

export interface PremiumTrialModalProps {
  onTrialExtensionClick: () => void;
  onCtaClick: () => void;
  isAdmin: boolean;
  ctaLeadsToBoard?: boolean;
  titleId?: string;
}

export const PremiumTrialModal: FunctionComponent<PremiumTrialModalProps> = ({
  onTrialExtensionClick,
  onCtaClick,
  isAdmin,
  ctaLeadsToBoard = true,
  titleId = 'premium-trial-modal-title',
}) => {
  const personalProductivityCohort = usePersonalProductivityCohortValue();

  const isInPPCohort =
    personalProductivityCohort === 'ga' ||
    personalProductivityCohort === 'opted_in';

  const imageSource = isInPPCohort
    ? premiumTrialModalPPWelcomeImageSrc
    : premiumTrialModalWelcomeImagePngSrc;

  const textBody = isInPPCohort ? (
    <FormattedMessage
      id="templates.premium_trial.modal-description-new-pp"
      defaultMessage="This workspace has Trello Premium free for 14 days - with no credit card required. Enjoy productivity-boosting features like AI, planner (full access), card mirroring, unlimited boards, list colors, and more."
      description="modal description"
    />
  ) : (
    <FormattedMessage
      id="templates.premium_trial.modal-description"
      defaultMessage="This Workspace has Trello Premium free for 14 days—with no credit card required. Enjoy productivity-boosting features like views, advanced checklists, unlimited boards and collaborators, and list colors."
      description="modal description"
    />
  );

  const _onCtaClick = useCallback(() => {
    onCtaClick();
    Analytics.sendClickedButtonEvent({
      buttonName: 'acceptReverseTrialModalButton',
      source: 'announceReverseTrialModalScreen',
    });
  }, [onCtaClick]);

  const _onTrialExtensionClick = useCallback(() => {
    onTrialExtensionClick();
    Analytics.sendClickedButtonEvent({
      buttonName: 'extendReverseTrialModalButton',
      source: 'announceReverseTrialModalScreen',
    });
  }, [onTrialExtensionClick]);

  useEffect(() => {
    Analytics.sendScreenEvent({
      name: 'announceReverseTrialModalScreen',
      attributes: {
        userViewedFrom: ctaLeadsToBoard ? 'boardScreen' : 'workspaceHome',
      },
    });
  }, [ctaLeadsToBoard]);

  return (
    <div className={styles.ReverseTrialModal}>
      <img
        className={cx(styles.image, {
          [styles.gaImage]: personalProductivityCohort === 'ga',
        })}
        src={imageSource}
        alt=""
        data-testid="image"
      />
      <div className={styles.contentContainer}>
        <PremiumLozenge />
        <h1 className={styles.title} id={titleId}>
          <FormattedMessage
            id="templates.premium_trial.modal-title"
            defaultMessage="You're starting with Trello Premium!"
            description="modal title"
          />
        </h1>
        <p className={styles.description}>{textBody}</p>
        {!dontUpsell() && isAdmin && (
          <button
            className={styles.trialExtension}
            onClick={_onTrialExtensionClick}
            data-testid={getTestId<OnboardingIds>('trial-extension')}
          >
            <FormattedMessage
              id="templates.premium_trial.modal-trial-extension"
              defaultMessage="Extend your trial to 30 days by adding a payment method"
              description="modal trial extension cta"
            />
          </button>
        )}
        <Button
          className={styles.ctaButton}
          appearance="primary"
          onClick={_onCtaClick}
        >
          {ctaLeadsToBoard ? (
            <FormattedMessage
              id="templates.premium_trial.modal-board-cta"
              defaultMessage="Go to your first board"
              description="modal cta to board"
            />
          ) : (
            <>
              <FormattedMessage
                id="templates.premium_trial.modal-generic-cta"
                defaultMessage="You're all set up"
                description="modal cta to board"
              />
              <>{' 🎉'}</>
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
